import { supabase } from './supabase';

export interface AtlasParsedItem {
  productCode: string;
  aantal: number;
  verkoopprijsPerStuk: number;
  omschrijving?: string;
  aiInferredCode?: boolean;
  sfPrijs?: number;
  delta?: number;
}

export interface AtlasParseResult {
  inkoopordernummer: string;
  datum?: string | null;
  pdfTotaalExBtw?: number | null;
  pdfRetourMededeling?: string | null;
  items: AtlasParsedItem[];
}

export interface AtlasSubmitInput {
  parentAccountId: string;
  inkoopordernummer: string;
  pdfRetourMededeling?: string;
  pdfTotaal?: number;
  items: Array<{
    productCode: string;
    aantal: number;
    verkoopprijsPerStuk: number;
    omschrijving?: string;
  }>;
}

export interface AtlasSubmitResult {
  workOrderId: string;
  workOrderNumber: string;
  itemsCreated: number;
}

export class AtlasInkooporderError extends Error {
  code: string;
  constructor(code: string, message: string) {
    super(message);
    this.code = code;
  }
}

async function getToken(): Promise<string | null> {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}

async function fileToBase64(file: File): Promise<string> {
  const buf = await file.arrayBuffer();
  // Browser-veilige base64 voor Uint8Array. Chunked om stack-overflow op grote
  // PDFs te voorkomen.
  const bytes = new Uint8Array(buf);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, Array.from(bytes.subarray(i, i + chunkSize)));
  }
  return btoa(binary);
}

export async function parseAtlasPdf(file: File): Promise<AtlasParseResult> {
  if (!/\.pdf$/i.test(file.name) && file.type !== 'application/pdf') {
    throw new AtlasInkooporderError('INVALID_FILE', 'Bestand moet een PDF zijn');
  }
  const token = await getToken();
  if (!token) throw new AtlasInkooporderError('AUTH', 'Niet ingelogd');

  const pdfBase64 = await fileToBase64(file);

  let res: Response;
  try {
    res = await fetch('/api/orders/parse-atlas-pdf', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ pdfBase64, filename: file.name }),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new AtlasInkooporderError('NETWORK', msg);
  }

  let data: {
    success?: boolean;
    parsed?: AtlasParseResult;
    errorCode?: string;
    errorMessage?: string;
    error?: string;
    code?: string;
  };
  try {
    data = await res.json();
  } catch {
    throw new AtlasInkooporderError('PARSE', `Server respons ongeldig (status ${res.status})`);
  }

  if (!res.ok || !data.success || !data.parsed) {
    throw new AtlasInkooporderError(
      data.errorCode || data.code || 'UNKNOWN',
      data.errorMessage || data.error || 'Parsen mislukt',
    );
  }
  return data.parsed;
}

export async function submitAtlasInkooporder(input: AtlasSubmitInput): Promise<AtlasSubmitResult> {
  const token = await getToken();
  if (!token) throw new AtlasInkooporderError('AUTH', 'Niet ingelogd');

  let res: Response;
  try {
    res = await fetch('/api/orders/create-atlas-inkooporder', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(input),
    });
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err);
    throw new AtlasInkooporderError('NETWORK', msg);
  }

  let data: {
    success?: boolean;
    workOrderId?: string;
    workOrderNumber?: string;
    itemsCreated?: number;
    errorCode?: string;
    errorMessage?: string;
    error?: string;
    code?: string;
  };
  try {
    data = await res.json();
  } catch {
    throw new AtlasInkooporderError('PARSE', `Server respons ongeldig (status ${res.status})`);
  }

  if (!res.ok || !data.success) {
    throw new AtlasInkooporderError(
      data.errorCode || data.code || 'UNKNOWN',
      data.errorMessage || data.error || 'Aanmaken mislukt',
    );
  }
  return {
    workOrderId: data.workOrderId!,
    workOrderNumber: data.workOrderNumber!,
    itemsCreated: data.itemsCreated ?? input.items.length,
  };
}
